import { useLayoutEffect, useState } from "react";
import { chainStore } from "@/stores/chain/store";
import ChainCardMeta from "@/components/shopwindow/ChainCardMeta";
import { cleanChainTitle } from "@/modules/chainTitle";
import { chainThumbUrl } from "@/modules/chainThumb";
import { isChainActive } from "@/modules/chainReveal";
import { eventNames } from "@/modules/Constants";
import * as analytics from "@/modules/analytics";
import chainImpressions from "@/modules/chainImpressions";
import type { ExperienceId } from "@/types/experience";
import type { ExperienceState } from "@/types/state";

type UpNextListProps = {
  experienceState: ExperienceState;
  heading?: string;
  onApplicationEvent?: (event: Record<string, unknown>) => void;
};

/**
 * The persistent "Up Next" recommendation list in the shop-window panel. It
 * reads the prefetched chain store and renders nothing until the chain is
 * revealed and holds a recommendation besides the Up Next pick.
 */
// display, padding, colour, background and border stay in up-next.css: `button` (common.css) and .highlight-fg-1 (dynamic.css) would win over a utility
const ITEM =
  "tw:grid tw:w-full tw:cursor-pointer tw:grid-cols-[84px_1fr] tw:items-center tw:gap-[12px] tw:rounded-[9px] tw:text-left tw:[font-family:inherit] tw:[transition:border-color_0.12s,transform_0.12s,box-shadow_0.12s] tw:[&:hover]:[box-shadow:0_4px_12px_rgba(0,0,0,0.08)] tw:[&:hover]:[transform:translateY(-1px)]";

export default function UpNextList({
  experienceState,
  heading,
  onApplicationEvent,
}: UpNextListProps) {
  // A new object on every change, so the re-render happens even though the
  // store hands out the same chain array (a class setState always re-rendered).
  const [state, setState] = useState(() => ({
    chain: chainStore.getChain(),
  }));

  // componentDidMount subscribed from the layout phase; keep the same timing.
  useLayoutEffect(
    () =>
      chainStore.subscribe(() => {
        setState({
          chain: chainStore.getChain(),
        });
      }),
    [],
  );

  // The first chain item is the Up Next block's pick, so the list shows the rest.
  const items = (state.chain || []).slice(1);

  // Revealed AND at least one recommendation after dropping the Up Next pick.
  // The same decision drives render and the impression below.
  const isShown = () =>
    isChainActive(experienceState.applicationData) && items.length > 0;

  // componentDidMount and componentDidUpdate: fires the impression from the
  // commit, not from render, deciding again against the live application data.
  useLayoutEffect(() => {
    if (!isShown()) {
      return;
    }
    const experienceData = experienceState.experienceData;
    const currentExperienceId = experienceData
      ? experienceData.idexperience
      : null;
    if (chainImpressions.markOnce("list-shown", currentExperienceId)) {
      analytics.track("Chain List Shown", {
        pix_list_size: items.length,
      });
    }
  });

  const advance = (idexperience: ExperienceId, rank: number) => {
    analytics.track("Chain Recommendation Clicked", {
      pix_to_idexperience: idexperience,
      pix_rank: rank,
    });
    if (onApplicationEvent) {
      onApplicationEvent({
        name: eventNames.CHAIN_ADVANCE,
        idexperience: idexperience,
        region: "Up Next",
      });
    }
  };

  if (!isShown()) {
    return null;
  }

  return (
    <div className="up-next tw:mt-[14px] tw:px-[16px] tw:pt-[12px] tw:[border-top:1px_solid_rgba(200,200,200,0.5)]">
      <p className="up-next__heading tw:font-[700] tw:text-[#c43e3e] tw:text-[11px] tw:uppercase tw:tracking-[0.06em] tw:[margin:0_0_10px]">
        {heading || "Up Next"}
      </p>
      <div className="up-next__list tw:flex tw:flex-col tw:gap-[10px] tw:pb-[12px]">
        {items.map((item, index) => {
          const thumbUrl = chainThumbUrl(
            experienceState.experienceData,
            item.image,
          );
          const thumbStyle = thumbUrl
            ? { backgroundImage: `url(${thumbUrl})` }
            : undefined;
          return (
            <button
              type="button"
              key={item.experience_id}
              className={`up-next__item highlight-fg-1 ${ITEM}`}
              onClick={() => advance(item.experience_id, index)}
            >
              <span
                className="up-next__thumb tw:relative tw:aspect-[16/10] tw:overflow-hidden tw:rounded-[4px] tw:[background:linear-gradient(135deg,#c9772f,#8a4a1e)_center/cover]"
                style={thumbStyle}
                aria-hidden="true"
              >
                <span className="up-next__play tw:absolute tw:top-[50%] tw:left-[50%] tw:h-0 tw:w-0 tw:[border-bottom:8px_solid_transparent] tw:[border-left:13px_solid_rgba(255,255,255,0.9)] tw:[border-top:8px_solid_transparent] tw:[transform:translate(-40%,-50%)]" />
              </span>
              <span className="up-next__body tw:min-w-0">
                <span className="up-next__title content tw:block tw:leading-[1.25]">
                  <span className="strong">{cleanChainTitle(item.title)}</span>
                </span>
                {item.publisher ? (
                  <span className="up-next__pub content tw:mt-[3px] tw:block">
                    {item.publisher}
                  </span>
                ) : null}
                <ChainCardMeta item={item} />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
